import { RadioButton } from '@lt/components';
import cn from 'classnames';
import React from 'react';
import { renderToString } from 'react-dom/server';

import type { PaymentMethod as PaymentMethodType } from '@lt/api/dist/methods/orders/types/paymentMethods';
import type { StatsPayRequest } from '@lt/api/dist/methods/stats/types';
import { Warning as WarningIcon } from '@lt/components/icons-react';
import { PAYMENT_METHOD_MIR_CARD } from 'src/store/payment/constants';
import { getPaymentGroup } from '../../../utils/orderUtils';
import styles from './paymentMethod.module.css';

import ExpandedPaymentMethod from './components/ExpandedPaymentMethod';

import {
  PAYMENT_METHOD_TITLE_TEST_ID,
  PAYMENT_METHOD_RADIO_TEST_ID,
  PAYMENT_METHOD_DESCRIPTION_TEST_ID,
  PAYMENT_METHOD_ALT_LABEL_TEST_ID,
} from './constants';
import PaymentMethodIcon from './components/PaymentMethodIcon';

interface PaymentMethodProps {
  item: PaymentMethodType;
  isActive?: boolean;
  isDisabled?: boolean;
  isMobile: boolean;
  onChange?: (type: StatsPayRequest['payment_method']) => void;
  hasMirPayment?: boolean;
}

const PaymentMethod = ({
  item,
  isActive,
  onChange,
  isMobile = false,
  hasMirPayment = false,
  isDisabled,
}: PaymentMethodProps): JSX.Element => {
  const { type, description, label, altLabel, amount } = item;
  const paymentGroup = getPaymentGroup(type);

  const editedDescription = isDisabled
    ? `${renderToString(
        <WarningIcon className={styles.warningIcon} viewBox="0 0 13 13" />,
      )}${description}`
    : `${description}`;

  return (
    <div className={styles.container}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        className={styles.title}
        onClick={() => onChange?.(type)}
        role="button"
        tabIndex={0}
        data-testid={PAYMENT_METHOD_TITLE_TEST_ID}
        data-state={isActive ? 'active' : undefined}
      >
        {onChange && (
          <RadioButton
            name="payment"
            value={type}
            id={type}
            checked={isActive}
            disabled={isDisabled}
            data-testid={`${PAYMENT_METHOD_RADIO_TEST_ID}_${type}`}
            data-state={isActive ? 'active' : undefined}
          />
        )}

        <div>
          <label htmlFor={type}>
            <div
              className={cn(styles.label, {
                [styles.mobileLabel]: isMobile,
                [styles.disabledLabel]: isDisabled,
              })}
            >
              {label}
              <PaymentMethodIcon isMobile={isMobile} type={type} />
            </div>
          </label>
          {isActive && (
            <div>
              {/* Дополнительное описание */}
              {type.includes(PAYMENT_METHOD_MIR_CARD) && (
                <div
                  className={cn({ [styles.disabledLabel]: isDisabled })}
                  data-testid={`${PAYMENT_METHOD_ALT_LABEL_TEST_ID}_${type}`}
                  data-state={isActive ? 'active' : undefined}
                >
                  {altLabel}
                </div>
              )}
              {/* Основное описание */}
              {description && (
                <div
                  className={cn(styles.description, {
                    [styles.mobileDescription]: isMobile,
                  })}
                  data-testid={`${PAYMENT_METHOD_DESCRIPTION_TEST_ID}_${type}`}
                  data-state={isActive ? 'active' : undefined}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: editedDescription }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {isActive && (
        <ExpandedPaymentMethod
          type={type}
          hasMirPayment={hasMirPayment}
          additionalData={'additionalData' in item ? item.additionalData : null}
          amount={amount}
          group={paymentGroup}
          onChange={onChange}
        />
      )}
    </div>
  );
};

export default PaymentMethod;
